import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useTrack } from '@hackler/react-sdk';
import useRequest from '@hooks/useRequest';
import useQueryString from '@hooks/useQueryString';
import {
  CapaErrorCode,
  CapaPcbDetail,
  CapaPcbServiceType,
  CapaQuotationServiceType,
  DesignServiceInput,
  PcbServiceInput,
  RegisterRfqInput,
  RfqOptionalServiceInput,
  useRegisterRfqMutation,
  useRequestRfqIdQuery
} from '@generated/graphql';
import { useSnackbar } from '@components/Provider';
import LoadingPresenter from '@components/Loading';
import PCBStepProps from './RequestTypes/PCBStepRequest/types';
import { PcbRequestStageEnum, RequestQueryString } from './types';
import RfqDetail from '@routes/Common/RfqDetail';

const RequestPreview: React.FC<PCBStepProps> = ({ nextButtonAction }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const rfqId = useRef<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const {
    query: { process, serviceType, stage },
    onUpdateQueryString
  } = useQueryString<RequestQueryString>();
  const track = useTrack();
  const { requestState, onInitRfqState, onUpdateRfqInfo } = useRequest();
  const { enqueueSnackbar } = useSnackbar();
  const processes = process?.split(',');
  const hasDesign = processes?.includes(CapaPcbServiceType.CircuitDesign);
  const hasFabrication = processes?.includes(
    CapaPcbServiceType.CircuitFabrication
  );
  const hasAssembly = processes?.includes(CapaPcbServiceType.CircuitAssembly);
  useRequestRfqIdQuery({
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      onUpdateRfqInfo({ rfqId: result.requestRfqId?.data?.uuid });
      rfqId.current = result.requestRfqId?.data?.uuid ?? '';
    }
  });

  const onConvertData = useCallback(
    (data: RegisterRfqInput) => {
      const { pcb } = requestState;
      const { assemblyFiles, fabricationFiles } = pcb ?? {};
      const assemblyOnly =
        processes?.includes(CapaPcbServiceType.CircuitAssembly) &&
        processes.length === 1;
      const processTransform = (processes ?? []).map(
        (service) =>
          ({ serviceType: service } as PcbServiceInput | DesignServiceInput)
      );

      const designOptionalServices: RfqOptionalServiceInput[] = requestState.requestPrototyping
        ? [
            {
              serviceType: CapaQuotationServiceType.Prototyping,
              quantity: requestState?.prototypeQuantity
            }
          ]
        : [];

      const convertedData: RegisterRfqInput = {
        ...data,
        rfqId: rfqId.current,
        optionalServices: designOptionalServices,
        referenceFiles: requestState?.referenceFiles ?? [],
        serviceType: data?.serviceType ?? serviceType,
        publicOpen: data?.publicOpen ?? true,
        projectPurposeDetail: data?.projectPurposeDetail ?? '',
        design:
          data?.serviceType === CapaQuotationServiceType.Design
            ? {
                services: processTransform as DesignServiceInput[]
              }
            : undefined,
        pcb:
          data?.serviceType === CapaQuotationServiceType.Pcb
            ? {
                ...data.pcb,
                services: processTransform as PcbServiceInput[],
                metalMaskAvailablity:
                  !hasDesign && hasAssembly
                    ? data?.pcb?.metalMaskAvailablity
                    : undefined,
                materialSupplyMethod:
                  !hasDesign && hasAssembly
                    ? data?.pcb?.materialSupplyMethod
                    : undefined,
                assemblyMethod:
                  !hasDesign && hasAssembly
                    ? data?.pcb?.assemblyMethod
                    : undefined,
                assemblyFiles:
                  hasDesign || !hasAssembly
                    ? []
                    : assemblyFiles?.map((file) => ({
                        ...file,
                        quantity: Number(file.quantity)
                      })),
                fabricationFiles: hasDesign
                  ? []
                  : fabricationFiles?.map((file) => ({
                      ...file,
                      quantity: !assemblyOnly ? Number(file.quantity) : 0,
                      thickness: !assemblyOnly ? Number(file.thickness) : 0
                    })),
                assemblyQuantity:
                  hasDesign && hasAssembly
                    ? Number(data?.pcb?.assemblyQuantity) ?? 0
                    : undefined,
                fabricationQuantity:
                  hasDesign && hasFabrication
                    ? Number(data?.pcb?.fabricationQuantity) ?? 0
                    : undefined
              }
            : undefined
      };

      return convertedData;
    },
    [
      processes,
      requestState,
      hasAssembly,
      hasDesign,
      hasFabrication,
      serviceType
    ]
  );

  const [registerRfq] = useRegisterRfqMutation({
    onCompleted: (result) => {
      if (result?.registerRFQ?.result === CapaErrorCode.Success) {
        track({
          key: 'complete',
          properties: {
            serviceType: requestState.serviceType
          }
        });
        onInitRfqState();
        onUpdateQueryString({
          stage: PcbRequestStageEnum.COMPLETE
        });
        enqueueSnackbar('견적요청이 완료되었습니다.');
      } else {
        enqueueSnackbar('견적요청에 실패했습니다.');
      }
    },
    onError: (error) =>
      enqueueSnackbar('견적요청에 실패했습니다. 다시 시도해주세요')
  });

  const validation = () => {
    if (hasDesign) {
      if ((requestState.detailedRequest ?? '').length === 0) {
        enqueueSnackbar('상세 요청사항을 입력해주세요');
        return false;
      }

      if (hasFabrication) {
        if ((requestState.pcb?.fabricationQuantity || 0) === 0) {
          enqueueSnackbar('제작 수량을 입력해주세요');
          return false;
        }
      }

      if (hasAssembly) {
        if ((requestState.pcb?.assemblyQuantity || 0) === 0) {
          enqueueSnackbar('조립 수량을 입력해주세요');
          return false;
        }
      }
    } else {
      if (hasFabrication || hasAssembly) {
        const files = requestState.pcb?.fabricationFiles ?? [];
        if (files.length === 0) {
          enqueueSnackbar('제작 파일을 첨부해주세요');
          return false;
        }

        const hasNoQuantityFile = files.some(
          (file) => !file.quantity || file.quantity <= 0
        );
        const hasNoThicknessFile = files.some(
          (file) => !file.thickness || file.thickness <= 0
        );

        if (hasFabrication && (hasNoQuantityFile || hasNoThicknessFile)) {
          enqueueSnackbar('제작 파일의 두께와 수량을 입력해주세요');
          return false;
        }
      }

      if (hasAssembly) {
        const files = requestState.pcb?.assemblyFiles ?? [];
        const methods = requestState.pcb?.assemblyMethod ?? [];
        if (files.length === 0) {
          enqueueSnackbar('조립 파일을 첨부해주세요');
          return false;
        }

        const hasNoQuantityFile = files.some(
          (file) => !file.quantity || file.quantity <= 0
        );

        if (hasNoQuantityFile) {
          enqueueSnackbar('조립 파일의 수량을 입력해주세요');
          return false;
        }

        if (methods.length === 0) {
          enqueueSnackbar('조립 방식을 선택해주세요');
          return false;
        }
      }

      if (!requestState.projectName) {
        enqueueSnackbar('프로젝트명을 입력해주세요');
        return false;
      }

      if ((requestState.projectName ?? '').length > 20) {
        enqueueSnackbar('프로젝트명은 20자 이내로 입력해주세요');
        return false;
      }

      if (!requestState.maxEstimatedBudget) {
        enqueueSnackbar('추정예산범위를 입력해주세요');
        return false;
      }

      if (requestState.maxEstimatedBudget < 1) {
        enqueueSnackbar('추정예산에는 0이 들어갈 수 없습니다.');
        return false;
      }
    }
    return true;
  };

  const onSubmit = async () => {
    if (stage !== PcbRequestStageEnum.PREVIEW) return;
    if (!validation()) return;
    setLoading(true);

    const {
      uploadedDrawingFiles,
      uploadedReferenceFiles,
      detailed_request,
      isComplete,
      prototypeQuantity,
      requestPrototyping,
      isTest,
      ...rest
    } = requestState;

    await registerRfq({
      variables: {
        input: onConvertData(rest)
      }
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    nextButtonAction.current.PREVIEW = onSubmit;
  });

  return (
    <div>
      <LoadingPresenter open={loading} />
      <RfqDetail
        rfqId={requestState.rfqId ?? ''}
        isPreview={true}
        previewData={{
          ...requestState,
          optionalServices: requestState.requestPrototyping
            ? [
                {
                  serviceType: CapaQuotationServiceType.Prototyping,
                  quantity: requestState.prototypeQuantity
                }
              ]
            : [],
          pcb: (requestState?.pcb as CapaPcbDetail) ?? undefined,
          id: 'preview'
        }}
        matches={matches}
      />
    </div>
  );
};

export default RequestPreview;
